/**
 * SessionRecorder — Tier 3 of the SCENE design.
 *
 * Tier 1 (Playwright journeys) drives the app via headless Chromium.
 * Tier 2 (ingest-recording) takes a user-recorded video. Tier 3 captures
 * a real-user session IN-APP — the user toggles "record session" in
 * Settings, walks through whatever they want to demonstrate, taps stop,
 * and downloads a session.json that captures:
 *
 *   - Every user-driven click + the visible text / aria-label of the target
 *   - Every director surface-change (push/pop/bridge/spawn)
 *   - Every console error + pageerror
 *   - Per-event document.body.outerHTML snapshot (trimmed to 50KB)
 *
 * The session.json can then be:
 *   - Read directly (it's a chronological event log)
 *   - Reconstructed into a SCENE journey via `pnpm tier3:reconstruct`
 *
 * Why not getDisplayMedia + MediaRecorder? Two reasons:
 *   1. iOS Safari doesn't support getDisplayMedia at all
 *   2. The OS-level permission prompt is jarring + most users abandon
 *
 * Why not html2canvas snapshots? Optional next slice — for now the
 * outerHTML capture is enough for Claude to understand visual state
 * (it can read the DOM, infer the surface from class names + text,
 * and pair with the user's spoken intent).
 */

import React, { useEffect, useRef, useState } from 'react';
import type { AppContext } from '../app';

interface SessionEvent {
  /** Monotonic ms since recording start. */
  t: number;
  /** Wall-clock ISO timestamp for cross-reference. */
  at: string;
  kind: 'click' | 'surface-change' | 'console-error' | 'page-error' | 'note';
  /** Human-readable detail (target text, surface id, error message). */
  detail: string;
  /** Truncated DOM body outerHTML at the time of the event. */
  dom?: string;
}

interface SessionBundle {
  /** Recording session metadata. */
  meta: {
    startedAt: string;
    finishedAt: string;
    durationMs: number;
    userAgent: string;
    viewport: { width: number; height: number };
    identityFingerprint: string;
  };
  events: SessionEvent[];
}

const MAX_DOM_BYTES = 50_000;

export function SessionRecorder({ ctx, fingerprint }: { ctx: AppContext; fingerprint: string }): JSX.Element | null {
  const [recording, setRecording] = useState<boolean>(false);
  const [eventCount, setEventCount] = useState<number>(0);
  const eventsRef = useRef<SessionEvent[]>([]);
  const startRef = useRef<number>(0);
  // Detach functions for the listeners we install — stored so we can
  // tear down cleanly when the user stops recording.
  const cleanupRef = useRef<(() => void) | null>(null);

  const captureDom = (): string | undefined => {
    try {
      const html = document.body.outerHTML;
      return html.length > MAX_DOM_BYTES ? html.slice(0, MAX_DOM_BYTES) + '<!-- truncated -->' : html;
    } catch { return undefined; }
  };

  const record = (kind: SessionEvent['kind'], detail: string, includeDom = false): void => {
    const dom = includeDom ? captureDom() : undefined;
    const ev: SessionEvent = {
      t: Date.now() - startRef.current,
      at: new Date().toISOString(),
      kind, detail,
      ...(dom !== undefined && { dom }),
    };
    eventsRef.current.push(ev);
    setEventCount(eventsRef.current.length);
  };

  const start = (): void => {
    if (recording) return;
    eventsRef.current = [];
    startRef.current = Date.now();
    setRecording(true);
    record('note', 'recording-started', true);

    // Global click listener — captures every tap on the document.
    const onClick = (e: MouseEvent): void => {
      const t = e.target as HTMLElement | null;
      if (t === null) return;
      const text = (t.textContent ?? '').trim().slice(0, 80);
      const aria = t.getAttribute('aria-label') ?? '';
      const tag = t.tagName.toLowerCase();
      const cls = t.className && typeof t.className === 'string' ? t.className.split(/\s+/).slice(0, 2).join('.') : '';
      const detail = `<${tag}${cls ? '.' + cls : ''}> ${aria || text}`.slice(0, 120);
      record('click', detail, false);
    };

    // Surface change listener — wires into the director.
    const unsubDirector = ctx.director.subscribe((state) => {
      record('surface-change', `→ ${state.active ?? '(none)'} (stack ${state.stack.length} deep)`, true);
    });

    const onConsoleError = (e: ErrorEvent): void => {
      record('console-error', `${e.message} @ ${e.filename}:${e.lineno}`, false);
    };
    const onUnhandledRejection = (e: PromiseRejectionEvent): void => {
      const reason = e.reason instanceof Error ? e.reason.message : String(e.reason);
      record('page-error', `unhandled: ${reason}`, false);
    };

    window.addEventListener('click', onClick, true);
    window.addEventListener('error', onConsoleError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    cleanupRef.current = () => {
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('error', onConsoleError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      unsubDirector();
    };
  };

  const stop = (): void => {
    if (!recording) return;
    record('note', 'recording-stopped', true);
    cleanupRef.current?.();
    cleanupRef.current = null;

    const bundle: SessionBundle = {
      meta: {
        startedAt: new Date(startRef.current).toISOString(),
        finishedAt: new Date().toISOString(),
        durationMs: Date.now() - startRef.current,
        userAgent: navigator.userAgent,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        identityFingerprint: fingerprint,
      },
      events: eventsRef.current,
    };

    // Download as JSON.
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alds-session-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setRecording(false);
    setEventCount(0);
    eventsRef.current = [];
  };

  // Add a hidden keyboard shortcut (Ctrl+Shift+R) for power users who
  // want to start recording before navigating to Settings → Recording.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'R' || e.key === 'r')) {
        e.preventDefault();
        if (recording) stop(); else start();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  if (!recording && eventCount === 0) {
    // Idle: render a small toggle in a fixed corner so users can find it
    // even if they haven't discovered the sass-more setting yet.
    return (
      <button className="session-rec-toggle" onClick={start} aria-label="Start recording session for feedback">
        ● rec
        <style>{toggleStyles}</style>
      </button>
    );
  }

  return (
    <div className="session-rec-active" role="status">
      <span className="session-rec-dot" aria-hidden="true">●</span>
      <span>rec · {eventCount} events</span>
      <button className="pill" onClick={stop} aria-label="Stop recording and download session bundle">stop + download</button>
      <style>{toggleStyles}</style>
    </div>
  );
}

const toggleStyles = `
.session-rec-toggle {
  position: fixed;
  top: 8px;
  right: 8px;
  z-index: 90;
  font-size: 9px;
  letter-spacing: 0.2em;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid var(--rule-bright);
  color: var(--ink-dim);
  text-transform: uppercase;
  cursor: pointer;
  opacity: 0.6;
}
.session-rec-toggle:hover { opacity: 1; color: var(--red); border-color: var(--red); }

.session-rec-active {
  position: fixed;
  top: 8px;
  right: 8px;
  z-index: 90;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  background: rgba(255, 90, 78, 0.1);
  border: 1px solid var(--red);
  color: var(--red);
  font-size: 10px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}
.session-rec-dot {
  color: var(--red);
  animation: session-rec-pulse 1s infinite;
}
@keyframes session-rec-pulse {
  50% { opacity: 0.3; }
}
@media (prefers-reduced-motion: reduce) {
  .session-rec-dot { animation: none; }
}
`;
